/**
 * 存储类型工具函数（支持 i18n）
 */
import i18n from 'i18next';

/**
 * 将英文存储类型转换为本地化显示名称
 * @param storageType 存储类型（TEMPORARY、NORMAL、LONG_TERM）
 * @returns 本地化显示名称
 */
export const getStorageTypeDisplayName = (storageType?: string): string => {
  if (!storageType) {
    return '';
  }
  const keyMap: Record<string, string> = {
    'TEMPORARY': 'storageType.temporary',
    'NORMAL': 'storageType.normal',
    'LONG_TERM': 'storageType.longTerm'
  };
  const key = keyMap[storageType.toUpperCase()];
  return key ? i18n.t(key) : storageType;
};

/**
 * 获取存储类型的描述信息（本地化）
 * @param storageType 存储类型
 * @returns 描述信息
 */
export const getStorageTypeDescription = (storageType?: string): string => {
  if (!storageType) {
    return '';
  }
  const keyMap: Record<string, string> = {
    'TEMPORARY': 'storageType.save1Day',
    'NORMAL': 'storageType.save180Days',
    'LONG_TERM': 'storageType.longTermSave'
  };
  const key = keyMap[storageType.toUpperCase()];
  return key ? i18n.t(key) : '';
};

/**
 * 格式化日期为 YYYY-MM-DD 格式
 * @param date 日期对象或时间戳
 * @returns 格式化的日期字符串
 */
const formatDate = (date: Date | string | number): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 获取过期时间的显示文案（本地化）
 * 优先展示自定义过期时间，如果没有则按存储类型显示
 * @param storageType 存储类型
 * @param expireTime 自定义过期时间
 * @returns 显示文案
 */
export const getExpireTimeDisplay = (storageType?: string, expireTime?: Date | string | null): string => {
  if (expireTime) {
    const expireDate = new Date(expireTime);
    const now = new Date();
    const dateStr = formatDate(expireDate);

    if (expireDate < now) {
      return i18n.t('storageType.expired', { date: dateStr });
    }

    const diffTime = expireDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 30) {
      return i18n.t('storageType.daysLeft', { count: diffDays });
    }
    if (diffDays <= 365) {
      const months = Math.floor(diffDays / 30);
      return i18n.t('storageType.monthsLeft', { count: months });
    }
    return i18n.t('storageType.expireOnDate', { date: dateStr });
  }

  return getStorageTypeDescription(storageType);
};

